/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux';
import { setCalendarPrices, setCalendarEurPrices, setDiscardedCities, setFrom, setTo } from '@/redux/bookingSlice';
import { resetExtrasStates } from '@/redux/extrasSlice';
import { addAvailableExtras, setAvailableCities } from '@/redux/planSlice';
import GlobalButton from '@/components/globals/globalButton/GlobalButton';
import OriginSelector from '@/components/globals/selectors/OriginSelector';
import PeopleSelector from '@/components/globals/selectors/PeopleSelector';
import DaysSelector from '@/components/globals/selectors/DaysSelector';
import PlanSelector from '@/components/globals/selectors/PlanSelector';
import FunnelTypeSelector from './funnelTypeSelector/FunnelTypeSelector';
import BudgetSelector from './budgetSelector/BudgetSelector';
import useTracking from '@/hooks/useTracking';
import handleError from '@/utils/handleError';
import DateSelector from '@/components/globals/selectors/DateSelector';
import CitySelector from '@/components/globals/selectors/CitySelector';
import getSelectedPlan from '@/utils/getSelectedPlan';
import useRoutes from '@/hooks/useRoutes';
import APIKit from '@/utils/APIKit';
import styles from './SelectorsContainer.module.scss';
import usePriceCurrency from '@/hooks/usePriceCurrency';
import usePlan from '@/hooks/usePlan';
import { adaptInsurances } from '@/utils/adapters/insurancesAdapter';

interface CustomState {
    from: string;
}

const SelectorsContainer = () => {
    const [errorMessage, setErrorMessage] = useState({ visible: false, message: '' });
    const currentPlanId = useAppSelector((state) => state.plan.currentPlan);
    const { isAloyPlan, isGetawayPlan } = getSelectedPlan(currentPlanId);
    const { origin, destination, days, currency, from, to } = useAppSelector((state) => state.booking);
    const { planConfig, getPlanFromPrice } = usePlan();
    const planBasePrice = getPlanFromPrice({ showMinPrice: false });
    const locationState = useLocation().state as CustomState;
    const isRedeemingAGift = useAppSelector((state) => state.gift.isRedeemingAGift);
    const isFromLandingCard = locationState?.from === 'landing';
    const showPlanSelector = !isRedeemingAGift && !isFromLandingCard;
    const hasFlightHotel = !isGetawayPlan;
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const { routes, currentPathname, isHomePage } = useRoutes();
    const { trackStartFunnelEvent } = useTracking();
    const { getPriceCurrency } = usePriceCurrency();
    const basePrice = getPriceCurrency(planBasePrice);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        if (!errorMessage.visible) return;
        setErrorMessage({ visible: false, message: '' });
    }, [isAloyPlan, origin.id, destination?.id]);

    useEffect(() => {
        if (isAloyPlan) {
            dispatch(setAvailableCities([]));
        }
    }, [currentPlanId]);

    const getCalendarPrices = async () => {
        await APIKit.get(`/calendar/${currentPlanId}/${days}/${origin.id}/?target_currency=${currency.name}`)
            .then((response) => {
                dispatch(setCalendarPrices(response.data));
            })
            .catch((error) => {
                const errorMsg = handleError(error);
                setErrorMessage({ visible: true, message: errorMsg });
                setButtonDisabled(false);
            });

        if (currency.name !== 'EUR') {
            await APIKit.get(`/calendar/${currentPlanId}/${days}/${origin.id}/?target_currency=EUR`)
                .then((response) => {
                    dispatch(setCalendarEurPrices(response.data));
                })
                .catch((error) => {
                    const errorMsg = handleError(error);
                    setErrorMessage({ visible: true, message: errorMsg });
                    setButtonDisabled(false);
                });
        }
    };

    const getInsurancesData = async () => {
        await APIKit.get(`/travels/insurance/${days}/${currentPlanId}/?target_currency=${currency.name}`)
            .then((res) => {
                const insurancesData = adaptInsurances(res.data);
                dispatch(addAvailableExtras(insurancesData));
            })
            .catch((error) => {
                const errorMsg = handleError(error);
                console.log(errorMsg);
            });
    };

    const onStartClick = async () => {
        setButtonDisabled(true);
        if (!isAloyPlan) {
            dispatch(setDiscardedCities([]));
            dispatch(resetExtrasStates());
            dispatch(setTo(''));
            dispatch(setFrom(''));
            getInsurancesData();
            await getCalendarPrices();
            trackStartFunnelEvent(from);
            navigate(routes.calendar);
        } else {
            if (!from || !to) {
                setErrorMessage({ visible: true, message: 'Please select travel dates' });
                setButtonDisabled(false);
                return;
            }
            if (origin.id === 0 || destination.id === 0) {
                setErrorMessage({ visible: true, message: 'Please select origin and destination' });
                setButtonDisabled(false);
                return;
            }
            navigate(routes.bestResult);
        }
    };

    const FlightHotel = () => (
        <Trans i18nKey="flightHotelPrice" basePrice={basePrice}>
            <span className={styles.boldText}>Flights + Hotel</span>
            from
            <span className={`${styles.coloured} ${styles.bigLetter}`}>
                {/* @ts-ignore <-- Hasta que i18next solucione problema con el componente Trans */}
                {{ basePrice }}
            </span>
        </Trans>
    );

    const AccommodationTravel = () => (
        <Trans i18nKey="accommodationTravelPrice" basePrice={basePrice}>
            <span className={styles.boldText}>Accommodation + Travel route</span>
            from
            <span className={`${styles.coloured} ${styles.bigLetter}`}>
                {/* @ts-ignore <-- Hasta que i18next solucione problema con el componente Trans */}
                {{ basePrice }}
            </span>
        </Trans>
    );

    const getTitle = () => {
        if (isAloyPlan) return <span className={styles.aloyTitle}>{t('flightsPlusHotel')}</span>;
        if (isRedeemingAGift) return <span className={styles.boldText}>{t('startBookingRedeem')}</span>;
        if (hasFlightHotel) return <FlightHotel />;
        return <AccommodationTravel />;
    };

    const getSubtitle = () => {
        if (isAloyPlan) return <span className={styles.aloySubtitle}>{t('travelWhereverYouWant')}</span>;

        return (
            <Trans i18nKey={planConfig.subtitle}>
                Discover your destination <span>2 days before</span> leaving!
            </Trans>
        );
    };

    return (
        <div className={styles.selectorsContainer}>
            {isHomePage && isAloyPlan && <FunnelTypeSelector />}
            <p className={styles.titleBox}>{getTitle()}</p>
            <p className={styles.coverSubtitle}>{getSubtitle()}</p>
            <div className={styles.selectorsWrapper}>
                {!isAloyPlan && showPlanSelector && <PlanSelector />}
                {isAloyPlan ? <CitySelector type="origin" /> : <OriginSelector />}
                {isAloyPlan && <CitySelector type="destination" />}
                {isAloyPlan ? <DateSelector /> : <DaysSelector />}
                <PeopleSelector mode={isAloyPlan ? 'compact' : 'standard'} />
                <div className={`${styles.buttonContainerFlykube}`}>
                    <GlobalButton isDisabled={buttonDisabled} onClick={onStartClick} className={styles.startBtn}>
                        {t('start')}
                    </GlobalButton>
                </div>
            </div>

            {errorMessage.visible && <p className={styles.errorMessage}>{errorMessage.message}</p>}
        </div>
    );
};

export default SelectorsContainer;
